/**
 * Standalone Cases page - list all cases with filters and Add case
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TableSortLabel,
  Chip,
  IconButton,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  InputAdornment,
} from '@mui/material';
import {
  Add as AddIcon,
  Search as SearchIcon,
  Visibility as ViewIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { Case, CaseFilters } from '../types/case';
import apiService from '../services/api';
import CaseFormDialog from '../components/CaseFormDialog';

type OptionItem = { value: string; label: string; sort_order: number };

const CasesPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [cases, setCases] = useState<Case[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [filters, setFilters] = useState<CaseFilters>({
    q: '',
    status: '',
    exclude_status: 'completed',
    case_type: '',
    assignee_id: '',
    sort_by: 'created_at',
    sort_order: 'desc',
  });
  const [users, setUsers] = useState<{ id: string; username: string; display_name: string }[]>([]);
  const [formOpen, setFormOpen] = useState(false);
  const [fieldOptions, setFieldOptions] = useState<{
    status: OptionItem[];
    case_reason: OptionItem[];
    priority: OptionItem[];
    case_origin: OptionItem[];
  }>({ status: [], case_reason: [], priority: [], case_origin: [] });

  const fetchCases = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: Record<string, any> = {
        page: page + 1,
        per_page: rowsPerPage,
        sort_by: filters.sort_by || 'created_at',
        sort_order: filters.sort_order || 'desc',
      };
      if (filters.q) params.q = filters.q;
      if (filters.status) params.status = filters.status;
      if (filters.exclude_status) params.exclude_status = filters.exclude_status;
      if (filters.case_type) params.case_type = filters.case_type;
      if (filters.assignee_id) params.assignee_id = filters.assignee_id;
      const res = await apiService.getCases(params);
      setCases(res.cases);
      setTotal(res.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to load cases');
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filters]);

  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  useEffect(() => {
    apiService.getUsers().then(setUsers).catch(() => {});
  }, []);

  useEffect(() => {
    apiService.getCaseFieldOptionsGrouped().then(setFieldOptions).catch(() => {});
  }, []);

  useEffect(() => {
    const s = searchParams.get('status');
    if (s) {
      setFilters((prev) => {
        if (prev.status === s && prev.exclude_status === undefined) return prev;
        return { ...prev, status: s, exclude_status: undefined };
      });
      setPage(0);
    }
  }, [searchParams]);

  type ChipColor = 'default' | 'primary' | 'success' | 'warning' | 'error';
  const toChipColor = (c?: string | null): ChipColor =>
    (c && ['default', 'primary', 'success', 'warning', 'error'].includes(c) ? c : 'default') as ChipColor;
  const statusOptions = fieldOptions.status.length > 0 ? fieldOptions.status : [];
  const statusLabelMap: Record<string, string> = statusOptions.length > 0
    ? Object.fromEntries(statusOptions.map((o) => [o.value, o.label]))
    : {};
  const statusColorMap: Record<string, ChipColor> = statusOptions.length > 0
    ? Object.fromEntries(statusOptions.map((o) => [o.value, toChipColor((o as { color?: string }).color)]))
    : {};
  const priorityLabelMap: Record<string, string> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, o.label]))
    : {};
  const priorityColorMap: Record<string, ChipColor> = fieldOptions.priority.length > 0
    ? Object.fromEntries(fieldOptions.priority.map((o) => [o.value, toChipColor((o as { color?: string }).color)]))
    : {};

  const caseTypeLabel: Record<string, string> = {
    service: 'Service',
    billing: 'Billing',
    other: 'Other',
  };

  const handleClearFilters = () => {
    setFilters({ ...filters, q: '', status: '', exclude_status: 'completed', case_type: '', assignee_id: '' });
    setSearchParams((sp) => {
      const n = new URLSearchParams(sp);
      n.delete('status');
      return n;
    }, { replace: true });
    setPage(0);
  };

  const handleSort = (field: string) => {
    const isAsc = filters.sort_by === field && filters.sort_order === 'asc';
    setFilters({ ...filters, sort_by: field, sort_order: isAsc ? 'desc' : 'asc' });
    setPage(0);
  };

  const handleCaseCreated = () => {
    setFormOpen(false);
    fetchCases();
  };

  const formatDate = (s: string | null | undefined) => {
    if (!s) return '-';
    return new Date(s).toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" component="h1" fontWeight={600}>
          Cases
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setFormOpen(true)}
        >
          Add case
        </Button>
      </Box>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search subject, case number..."
            value={filters.q || ''}
            onChange={(e) => setFilters({ ...filters, q: e.target.value })}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 200 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={!filters.status && filters.exclude_status === 'completed' ? '__exclude_completed__' : (filters.status || '')}
              label="Status"
              onChange={(e) => {
                const v = e.target.value;
                if (v === '__exclude_completed__') {
                  setFilters({ ...filters, status: '', exclude_status: 'completed' });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.delete('status');
                    return n;
                  }, { replace: true });
                } else if (v) {
                  setFilters({ ...filters, status: v, exclude_status: undefined });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.set('status', v);
                    return n;
                  }, { replace: true });
                } else {
                  setFilters({ ...filters, status: '', exclude_status: undefined });
                  setSearchParams((sp) => {
                    const n = new URLSearchParams(sp);
                    n.delete('status');
                    return n;
                  }, { replace: true });
                }
                setPage(0);
              }}
            >
              <MenuItem value="__exclude_completed__">All except Completed</MenuItem>
              <MenuItem value="">All</MenuItem>
              {statusOptions.map((o) => (
                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filters.case_type || ''}
              label="Type"
              onChange={(e) => {
                setFilters({ ...filters, case_type: e.target.value || undefined });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="service">Service</MenuItem>
              <MenuItem value="billing">Billing</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Assignee</InputLabel>
            <Select
              value={filters.assignee_id || ''}
              label="Assignee"
              onChange={(e) => {
                setFilters({ ...filters, assignee_id: e.target.value || undefined });
                setPage(0);
              }}
            >
              <MenuItem value="">All</MenuItem>
              {users.map((u) => (
                <MenuItem key={u.id} value={u.id}>
                  {u.display_name || u.username}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button size="small" startIcon={<ClearIcon />} onClick={handleClearFilters}>
            Clear
          </Button>
          <Button variant="contained" onClick={fetchCases} disabled={loading}>
            Apply
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        whiteSpace: 'nowrap',
                        minWidth: '7.5rem',
                        width: '1%',
                        verticalAlign: 'middle',
                      }}
                    >
                      <TableSortLabel
                        active={filters.sort_by === 'case_number'}
                        direction={filters.sort_by === 'case_number' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('case_number')}
                      >
                        Case #
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'subject'}
                        direction={filters.sort_by === 'subject' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('subject')}
                      >
                        Subject
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'case_type'}
                        direction={filters.sort_by === 'case_type' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('case_type')}
                      >
                        Type
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'status'}
                        direction={filters.sort_by === 'status' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('status')}
                      >
                        Status
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'priority'}
                        direction={filters.sort_by === 'priority' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('priority')}
                      >
                        Priority
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Assignee</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={filters.sort_by === 'due_by'}
                        direction={filters.sort_by === 'due_by' ? (filters.sort_order as 'asc' | 'desc') : 'asc'}
                        onClick={() => handleSort('due_by')}
                      >
                        Due
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {cases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={9} align="center" sx={{ py: 4 }}>
                        No cases found
                      </TableCell>
                    </TableRow>
                  ) : (
                    cases.map((c) => (
                      <TableRow key={c.id} hover>
                        <TableCell
                          sx={{
                            whiteSpace: 'nowrap',
                            minWidth: '7.5rem',
                            width: '1%',
                            verticalAlign: 'middle',
                            fontVariantNumeric: 'tabular-nums',
                          }}
                        >
                          {c.case_number}
                        </TableCell>
                        <TableCell>{c.subject}</TableCell>
                        <TableCell>{c.account_display_name || c.account_id}</TableCell>
                        <TableCell>{caseTypeLabel[c.case_type] || c.case_type}</TableCell>
                        <TableCell>
                          <Chip
                            label={statusLabelMap[c.status] || c.status}
                            size="small"
                            color={statusColorMap[c.status] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={priorityLabelMap[c.priority] || c.priority}
                            size="small"
                            color={priorityColorMap[c.priority] ?? 'default'}
                          />
                        </TableCell>
                        <TableCell>{c.assignee_name || '-'}</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center' }}>
                            {formatDate(c.due_by)}
                            {c.due_by && c.status !== 'completed' && new Date(c.due_by) < new Date() && (
                              <Chip label="Overdue" size="small" color="error" variant="outlined" />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => navigate(`/crm/cases/${c.id}`)}>
                            <ViewIcon />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[10, 25, 50, 100]}
            />
          </>
        )}
      </Paper>

      <CaseFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        onSuccess={handleCaseCreated}
        accountId={undefined}
      />
    </Box>
  );
};

export default CasesPage;
